import React, { useState, useEffect, useRef } from 'react';
import { FiCheck, FiX, FiAlertCircle, FiLoader, FiTruck, FiZap, FiStar, FiDollarSign, FiPackage, FiMapPin } from 'react-icons/fi';
import api from '../utils/api';
import './PincodeChecker.css';

const OPTION_CONFIG = {
  cheapest: {
    icon: <FiDollarSign />,
    color: '#22c55e',
    gradient: 'linear-gradient(135deg, #22c55e, #16a34a)',
    tagLine: 'Best Price',
  },
  fastest: {
    icon: <FiZap />,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    tagLine: 'Express',
  },
  bestRated: {
    icon: <FiStar />,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    tagLine: 'Top Rated',
  },
  alternative: {
    icon: <FiTruck />,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    tagLine: 'Alternative',
  },
};

function PincodeChecker({ onPincodeChange, initialPincode = '', weightGrams = 0, orderTotal = 0 }) {
  const [pincode, setPincode] = useState(initialPincode);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  // Track previous weight/value to detect meaningful changes
  const prevWeightRef = useRef(weightGrams);
  const prevValueRef = useRef(orderTotal);

  // Re-fetch rates when weight or order value changes significantly (and pincode was already checked)
  useEffect(() => {
    const weightChanged = Math.abs(weightGrams - prevWeightRef.current) > 100; // >100g change
    const valueChanged = Math.abs(orderTotal - prevValueRef.current) > 50; // >₹50 change

    if (deliveryInfo?.available && pincode.length === 6 && (weightChanged || valueChanged)) {
      prevWeightRef.current = weightGrams;
      prevValueRef.current = orderTotal;
      // Silently re-check with new weight/value (no loading spinner to avoid flicker)
      const recheck = async () => {
        try {
          const params = new URLSearchParams();
          if (weightGrams > 0) params.append('weight', weightGrams);
          if (orderTotal > 0) params.append('value', orderTotal);
          const qs = params.toString();
          const response = await api.get(`/delivery/check-pincode/${pincode}${qs ? '?' + qs : ''}`);
          if (response.data.success && response.data.data.available) {
            const data = response.data.data;
            setDeliveryInfo(data);

            // Self-delivery or zone-based: notify parent directly
            if (data.source === 'self' || !data.courierOptions || data.courierOptions.length === 0) {
              setSelectedOption(null);
              if (onPincodeChange) {
                onPincodeChange(pincode, data);
              }
            } else {
              // Shiprocket: re-select same option type if available, else fall back to cheapest
              const sameOpt = data.courierOptions.find(o => o.type === selectedOption);
              const picked = sameOpt || data.courierOptions[0];
              setSelectedOption(picked.type);
              if (onPincodeChange) {
                onPincodeChange(pincode, {
                  ...data,
                  deliveryCharge: picked.rate,
                  estimatedDays: picked.estimatedDays,
                  zoneName: picked.name,
                  selectedCourier: picked,
                });
              }
            }
          }
        } catch (err) {
          // Silently ignore — keep existing rates
        }
      };
      recheck();
    }
  }, [weightGrams, orderTotal]); // Only re-run when weight or value changes

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPincode(value);
    setError(null);
    setDeliveryInfo(null);
    setSelectedOption(null);
  };

  const checkPincode = async () => {
    if (pincode.length !== 6) {
      setError('Please enter a valid 6-digit pincode');
      setDeliveryInfo(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Pass cart weight & order value for accurate Shiprocket rates
      const params = new URLSearchParams();
      if (weightGrams > 0) params.append('weight', weightGrams);
      if (orderTotal > 0) params.append('value', orderTotal);
      const qs = params.toString();
      const response = await api.get(`/delivery/check-pincode/${pincode}${qs ? '?' + qs : ''}`);

      if (response.data.success) {
        const data = response.data.data;
        setDeliveryInfo(data);

        // Self-delivery or zone-based: no courier selection needed
        if (data.source === 'self' || !data.courierOptions || data.courierOptions.length === 0) {
          setSelectedOption(null);
          if (onPincodeChange) {
            onPincodeChange(pincode, data);
          }
        } else {
          // Auto-select cheapest courier option
          const defaultOpt = data.courierOptions[0]; // cheapest is always first
          setSelectedOption(defaultOpt.type);
          if (onPincodeChange) {
            onPincodeChange(pincode, {
              ...data,
              deliveryCharge: defaultOpt.rate,
              estimatedDays: defaultOpt.estimatedDays,
              zoneName: defaultOpt.name,
              selectedCourier: defaultOpt,
            });
          }
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to check pincode';
      setError(errorMsg);
      setDeliveryInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option.type);
    if (onPincodeChange && deliveryInfo) {
      onPincodeChange(pincode, {
        ...deliveryInfo,
        deliveryCharge: option.rate,
        estimatedDays: option.estimatedDays,
        zoneName: option.name,
        selectedCourier: option,
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      checkPincode();
    }
  };

  const renderStars = (rating) => {
    const stars = Math.round(rating * 2) / 2; // Round to nearest 0.5
    const full = Math.floor(stars);
    return (
      <span className="courier-stars">
        {'★'.repeat(Math.min(full, 5))}
        {'☆'.repeat(Math.max(5 - full, 0))}
        <span className="star-num">{rating > 0 ? rating.toFixed(1) : '--'}</span>
      </span>
    );
  };

  return (
    <div className="pincode-checker">
      <div className="pincode-input-group">
        <div className="pincode-input-wrap">
          <FiPackage className="pincode-field-icon" />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Enter delivery pincode"
            value={pincode}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            maxLength="6"
            className="pincode-input"
          />
        </div>
        <button
          onClick={checkPincode}
          disabled={pincode.length !== 6 || loading}
          className="check-button"
        >
          {loading ? (
            <>
              <FiLoader className="spinner-icon" />
              Checking...
            </>
          ) : (
            <>
              <FiTruck />
              Check
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="pincode-result error">
          <FiX className="result-icon error-icon" />
          <div className="result-text">{error}</div>
        </div>
      )}

      {deliveryInfo && (
        <>
          {deliveryInfo.available ? (
            <div className="pincode-result-block">
              {/* Header */}
              <div className="delivery-header">
                <FiCheck className="delivery-header-icon" />
                <div className="delivery-header-text">
                  <span className="delivery-header-title">Delivery Available!</span>
                  {deliveryInfo.courierCount > 0 && (
                    <span className="delivery-header-sub">
                      {deliveryInfo.courierCount} courier{deliveryInfo.courierCount > 1 ? 's' : ''} serve this pincode
                    </span>
                  )}
                </div>
              </div>

              {/* Self-delivery (local Thrissur) */}
              {deliveryInfo.source === 'self' ? (
                <div className="self-delivery-card">
                  <div className="self-delivery-badge">
                    <FiMapPin />
                    <span>Local Delivery</span>
                  </div>
                  <div className="self-delivery-name">{deliveryInfo.zoneName}</div>
                  <div className="self-delivery-eta">
                    <FiTruck className="self-delivery-eta-icon" />
                    <span>{deliveryInfo.estimatedDays}</span>
                  </div>
                  {deliveryInfo.freeDeliveryApplied || deliveryInfo.deliveryCharge === 0 ? (
                    <div className="self-delivery-free">
                      <span className="badge">Free Delivery</span>
                    </div>
                  ) : (
                    <div className="self-delivery-charge-row">
                      <span className="self-delivery-charge">₹{deliveryInfo.deliveryCharge}</span>
                      {deliveryInfo.freeDeliveryMinOrder > 0 && (
                        <span className="self-delivery-free-hint">
                          Free on orders above ₹{deliveryInfo.freeDeliveryMinOrder}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ) : deliveryInfo.courierOptions && deliveryInfo.courierOptions.length > 0 ? (
                /* Courier option cards (Shiprocket) */
                <div className="courier-options">
                  <p className="courier-options-label">Choose your shipping speed:</p>
                  <div className="courier-cards">
                    {deliveryInfo.courierOptions.map((option) => {
                      const config = OPTION_CONFIG[option.type] || OPTION_CONFIG.cheapest;
                      const isSelected = selectedOption === option.type;
                      return (
                        <button
                          key={option.type}
                          className={`courier-card ${isSelected ? 'courier-card--selected' : ''}`}
                          onClick={() => handleOptionSelect(option)}
                          style={{
                            '--card-color': config.color,
                            '--card-gradient': config.gradient,
                          }}
                        >
                          {/* Selection indicator */}
                          <div className="courier-radio">
                            <div className={`courier-radio-dot ${isSelected ? 'active' : ''}`} />
                          </div>

                          {/* Tag */}
                          <div className="courier-tag" style={{ background: config.gradient }}>
                            {config.icon}
                            <span>{config.tagLine}</span>
                          </div>

                          {/* Courier name */}
                          <div className="courier-name">{option.name}</div>

                          {/* Rating */}
                          <div className="courier-rating">
                            {renderStars(option.rating)}
                          </div>

                          {/* Delivery estimate */}
                          <div className="courier-eta">
                            <FiTruck className="courier-eta-icon" />
                            <span>{option.estimatedDays}</span>
                          </div>

                          {/* Price */}
                          <div className="courier-price">
                            <span className="courier-price-rs">₹</span>
                            <span className="courier-price-num">{option.rate}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* Zone-based fallback (single result) */
                <div className="zone-result">
                  <div className="detail-row">
                    <span className="detail-label">Zone:</span>
                    <span className="detail-value">{deliveryInfo.zoneName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Estimated Delivery:</span>
                    <span className="detail-value">{deliveryInfo.estimatedDays}</span>
                  </div>
                  {deliveryInfo.deliveryCharge > 0 ? (
                    <>
                      <div className="detail-row">
                        <span className="detail-label">Delivery Charge:</span>
                        <span className="detail-value charge">₹{deliveryInfo.deliveryCharge}</span>
                      </div>
                      {deliveryInfo.freeDeliveryMinOrder > 0 && (
                        <div className="detail-row free-delivery-info">
                          <FiAlertCircle className="info-icon" />
                          <span className="info-text">
                            Free delivery on orders above ₹{deliveryInfo.freeDeliveryMinOrder}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="detail-row free-delivery-badge">
                      <span className="badge">Free Delivery</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="pincode-result not-available">
              <FiAlertCircle className="result-icon alert-icon" />
              <div className="result-text">{deliveryInfo.message}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PincodeChecker;
